import { useState, useEffect, useCallback } from 'react';
import { Eye, ShoppingCart } from 'lucide-react';
import { ordersApi, Order } from '../../api/payments';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { Sheet, SheetBody, SheetContent, SheetDescription, SheetHeader } from '../../components/ui/sheet';
import { Pagination } from '../../components/shared/Pagination';
import {
  EmptyState,
  ErrorState,
  FilterChips,
  FormField,
  PageHeader,
  StatusPill,
  Toolbar,
  type FilterChip, UserLink } from '@/components/app';
import { getErrorMessage } from '@/lib/errors';
import { useDebouncedCallback } from '@/hooks/useDebouncedCallback';
import { ORDER_STATUS_LABELS, labelOf } from '@/lib/labels';
import { orderStatusTone } from '@/lib/status';
import {
  RowsSkeleton,
  SearchInput,
  SheetItem,
  SheetSection,
  SheetSkeleton,
  formatAmount,
  formatDateTime,
  shortId,
} from './listParts';

interface OrdersPageProps {
  /** Внутри раздела «Заказы и платежи»: заголовок страницы рисует обёртка. */
  embedded?: boolean;
}

const ITEMS_PER_PAGE = 10;
const STATUSES = ['pending', 'paid', 'cancelled', 'refunded'] as const;

function OrderStatus({ status }: { status: string }) {
  return (
    <StatusPill tone={orderStatusTone(status)} dot>
      {labelOf(ORDER_STATUS_LABELS, status)}
    </StatusPill>
  );
}

export default function OrdersPage({ embedded = false }: OrdersPageProps) {
  const [orders, setOrders] = useState<Order[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [loadError, setLoadError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [isLoading, setIsLoading] = useState(true);

  // Детали заказа — правая панель.
  const [detailId, setDetailId] = useState<string | null>(null);
  const [detail, setDetail] = useState<Order | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);

  const fetchOrders = useCallback(async () => {
    setIsLoading(true);
    setLoadError(null);
    try {
      const params: any = { page: currentPage, limit: ITEMS_PER_PAGE };
      if (searchQuery) params.search = searchQuery;
      if (statusFilter !== 'all') params.status = statusFilter;
      const response = await ordersApi.getAll(params);
      setOrders(response.items);
      setTotalPages(response.totalPages);
      setTotal(response.total);
    } catch (error: any) {
      setLoadError(getErrorMessage(error, 'Не удалось загрузить заказы'));
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, searchQuery, statusFilter]);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  // Запрос уходит через 300 мс после последнего символа; страница сбрасывается на первую.
  const applySearch = useDebouncedCallback((value: string) => { setSearchQuery(value); setCurrentPage(1); });
  const handleSearch = (value: string) => { setSearchInput(value); applySearch(value); };
  const handleStatusFilter = (value: string) => { setStatusFilter(value); setCurrentPage(1); };
  const clearSearch = () => { setSearchInput(''); setSearchQuery(''); setCurrentPage(1); };
  const resetFilters = () => { clearSearch(); setStatusFilter('all'); };

  const loadDetail = useCallback(async (id: string) => {
    setDetailLoading(true);
    setDetailError(null);
    try {
      const fullOrder = await ordersApi.getById(id);
      setDetail(fullOrder as Order);
    } catch (error: any) {
      setDetailError(getErrorMessage(error, 'Не удалось загрузить детали заказа'));
    } finally {
      setDetailLoading(false);
    }
  }, []);

  const handleView = (order: Order) => {
    setDetailId(order.id);
    setDetail(null);
    void loadDetail(order.id);
  };

  const chips: FilterChip[] = [
    ...(searchQuery ? [{ key: 'search', label: `Поиск: ${searchQuery}`, onRemove: clearSearch, removeLabel: 'Убрать фильтр: поиск' }] : []),
    ...(statusFilter !== 'all'
      ? [{
          key: 'status',
          label: `Статус: ${labelOf(ORDER_STATUS_LABELS, statusFilter)}`,
          onRemove: () => handleStatusFilter('all'),
          removeLabel: 'Убрать фильтр: статус',
        }]
      : []),
  ];

  return (
    <div className="flex flex-col gap-5">
      {!embedded && <PageHeader title="Заказы" subtitle="Заказы тарифов и их статусы" />}

      <Card role="search" aria-label="Фильтры заказов" className="grid grid-cols-1 gap-3 px-[18px] py-4 sm:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
        <FormField label="Поиск" tone="filter">
          <SearchInput
            placeholder="Имя, email, телефон или ID заказа"
            value={searchInput}
            onChange={(e) => handleSearch(e.target.value)}
          />
        </FormField>
        <FormField label="Статус" tone="filter">
          {(p) => (
            <Select value={statusFilter} onValueChange={handleStatusFilter}>
              <SelectTrigger {...p}>
                <SelectValue placeholder="Все статусы" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Все статусы</SelectItem>
                {STATUSES.map((s) => (
                  <SelectItem key={s} value={s}>{ORDER_STATUS_LABELS[s]}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </FormField>
      </Card>

      <Card className="overflow-hidden" role="region" aria-label="Список заказов">
        <Toolbar title={<>Найдено <span className="tabular-nums">{total.toLocaleString('ru-RU')}</span></>}>
          <FilterChips chips={chips} onReset={resetFilters} />
        </Toolbar>

        {isLoading ? (
          <RowsSkeleton label="Загрузка заказов" />
        ) : loadError ? (
          <ErrorState message={loadError} onRetry={fetchOrders} className="border-t border-border-soft" />
        ) : orders.length === 0 ? (
          <EmptyState
            className="border-t border-border-soft"
            icon={<ShoppingCart />}
            title="Заказы не найдены"
            description={chips.length ? 'Попробуйте изменить или сбросить фильтры.' : undefined}
            action={chips.length ? <Button variant="secondary" onClick={resetFilters}>Сбросить фильтры</Button> : undefined}
          />
        ) : (
          <Table className="min-w-[760px]">
            <TableHeader>
              <TableRow>
                <TableHead>Заказ</TableHead>
                <TableHead>Пользователь</TableHead>
                <TableHead className="hidden md:table-cell">Тариф</TableHead>
                <TableHead className="text-right">Сумма</TableHead>
                <TableHead>Статус</TableHead>
                <TableHead className="hidden sm:table-cell">Создан</TableHead>
                <TableHead className="text-right"><span className="sr-only">Действия</span></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {orders.map((order) => (
                <TableRow key={order.id} selected={detailId === order.id}>
                  <TableCell className="font-mono text-xs text-text-2">{shortId(order.id)}</TableCell>
                  <TableCell>
                    <UserLink user={order.user} />
                  </TableCell>
                  <TableCell className="hidden md:table-cell">{order.plan?.title || '—'}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex flex-col items-end gap-0.5">
                      <span className="whitespace-nowrap font-display font-bold tabular-nums">{formatAmount(order.final_amount, order.currency)}</span>
                      {order.discount_amount > 0 && (
                        <span className="whitespace-nowrap text-xs tabular-nums text-text-2">Скидка {formatAmount(order.discount_amount, order.currency)}</span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell><OrderStatus status={order.status} /></TableCell>
                  <TableCell className="hidden whitespace-nowrap tabular-nums text-text-2 sm:table-cell">{formatDateTime(order.created_at)}</TableCell>
                  <TableCell className="text-right">
                    <Button variant="secondary" size="icon-sm" aria-label={`Детали заказа ${shortId(order.id)}`} onClick={() => handleView(order)}>
                      <Eye />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        {!isLoading && !loadError && orders.length > 0 && (
          <Pagination currentPage={currentPage} totalPages={totalPages} total={total} itemsPerPage={ITEMS_PER_PAGE} onPageChange={setCurrentPage} />
        )}
      </Card>

      <Sheet open={detailId !== null} onOpenChange={(open) => { if (!open) setDetailId(null); }}>
        <SheetContent>
          <SheetHeader title="Детали заказа" />
          <SheetDescription className="sr-only">Полная информация о заказе</SheetDescription>
          <SheetBody>
            {detailLoading ? (
              <SheetSkeleton />
            ) : detailError ? (
              <ErrorState message={detailError} onRetry={() => detailId && void loadDetail(detailId)} />
            ) : detail ? (
              <>
                <section aria-label="Итог" className="flex items-start justify-between gap-3 rounded-card border border-border-soft bg-surface px-4 py-3.5 shadow-card">
                  <div className="flex min-w-0 flex-col gap-1">
                    <span className="text-xs text-text-2">Итоговая сумма</span>
                    <b className="font-display text-[22px] font-extrabold tabular-nums">{formatAmount(detail.final_amount, detail.currency)}</b>
                    <span className="text-[13px] text-text-2">{detail.plan?.title || 'Тариф не указан'}</span>
                  </div>
                  <OrderStatus status={detail.status} />
                </section>
                <SheetSection title="Заказ">
                  <SheetItem label="ID заказа" mono wide>{detail.id}</SheetItem>
                  <SheetItem label="Тариф">{detail.plan?.title || '—'}</SheetItem>
                  <SheetItem label="Пользователь" wide><UserLink user={detail.user} /></SheetItem>
                </SheetSection>
                <SheetSection title="Оплата">
                  <SheetItem label="Исходная сумма"><span className="tabular-nums">{formatAmount(detail.original_amount, detail.currency)}</span></SheetItem>
                  <SheetItem label="Скидка"><span className="tabular-nums">{formatAmount(detail.discount_amount, detail.currency)}</span></SheetItem>
                  <SheetItem label="Итоговая сумма"><span className="font-bold tabular-nums">{formatAmount(detail.final_amount, detail.currency)}</span></SheetItem>
                  {detail.promo_code && (
                    <SheetItem label="Промокод"><Badge variant="secondary" className="font-mono">{detail.promo_code.code}</Badge></SheetItem>
                  )}
                </SheetSection>
                <SheetSection title="Даты">
                  <SheetItem label="Создан"><span className="tabular-nums">{formatDateTime(detail.created_at)}</span></SheetItem>
                  <SheetItem label="Оплачен"><span className="tabular-nums">{formatDateTime(detail.paid_at)}</span></SheetItem>
                </SheetSection>
              </>
            ) : null}
          </SheetBody>
        </SheetContent>
      </Sheet>
    </div>
  );
}
